import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Divider,
  IconButton, Box, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import firebase from '../../../utilities/firebase';
import { StateManager } from '../../../utilities/stateManager';
import moment from 'moment';
import { useStackZIndex, ModalStackProvider } from '../../../utilities/ModalStackContext';

const SKYWAY_CLASSICS_ID = '9c0d88f5-84f9-454d-833d-a8ced9adad49';

const USD = (v) =>
  (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });

export default function ReceiveTradeModal({ open, onClose, trade, currentStock }) {
  const myZ = useStackZIndex();
  const [form, setForm] = React.useState({});
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (open && trade) {
      setForm({
        vin: trade.vin || '',
        year: trade.year ? String(trade.year) : '',
        make: trade.make || '',
        model: trade.model || '',
        color: '',
        equity: String((trade.trade || 0) - (trade.payoff || 0)),
      });
    }
  }, [open, trade]);

  const handleChange = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const grossTrade = trade?.trade || 0;
  const payoff = trade?.payoff || 0;
  const netTradePreview = grossTrade - payoff;

  const handleConfirm = async () => {
    // The VIN is optional here — it can be added from the new car's page later.
    if (!form.year || !form.make || !form.model || !form.color) {
      StateManager.setAlertAndOpen('Please fill in all vehicle fields', 'error');
      return;
    }
    const equity = parseFloat(form.equity) || 0;
    if (equity === 0) {
      StateManager.setAlertAndOpen('Trade equity must be non-zero', 'error');
      return;
    }

    setSaving(true);
    try {
      const db = firebase.firestore();
      const now = moment().format('YYYY/MM/DD');
      let newStock;
      let createdDeposit;

      await db.runTransaction(async (tx) => {
        const counterRef = db.doc('admin/counters');
        const counter = await tx.get(counterRef);
        const n = counter.data().lastStock + 1;
        newStock = 'SN' + n.toString();

        tx.set(db.doc('cars/' + newStock), {
          stock: newStock,
          vin: (form.vin || '').trim().toUpperCase(),
          year: parseInt(form.year, 10),
          make: form.make.trim(),
          model: form.model.trim(),
          color: form.color.trim(),
          status: 'intake',
          sub_status: 'inbound',
          status_time: now,
          sub_status_time: now,
          date: now,
          inDA: false,
          location: 'FL',
          consignor: SKYWAY_CLASSICS_ID,
        });

        tx.update(counterRef, { lastStock: n });

        const depositRef = db.collection('deposits').doc();
        const depositData = {
          d: depositRef.id,
          stock: currentStock,
          account: 'inventory',
          type: 'sales',
          date: now,
          amount: equity,
          memo: `Trade equity for ${newStock}`,
        };
        tx.set(depositRef, depositData);
        createdDeposit = { ...depositData, id: depositRef.id };

        const purchaseRef = db.collection('purchases').doc();
        tx.set(purchaseRef, {
          e: purchaseRef.id,
          stock: newStock,
          account: 'repair',
          type: 'sales',
          date: now,
          paidDate: now,
          vendor: 'Skyway Classics',
          amount: equity,
          memo: `Trade equity from ${currentStock}`,
          isPayable: false,
        });
      });

      StateManager.setAlertAndOpen(`Trade received — ${newStock} created`, 'success');
      onClose(newStock, createdDeposit);
    } catch (err) {
      console.error(err);
      StateManager.setAlertAndOpen('Failed to receive trade. Please try again.', 'error');
    }
    setSaving(false);
  };

  if (!trade) return null;

  return (
    <ModalStackProvider value={myZ}>
    <Dialog open={open} onClose={() => onClose(null)} maxWidth="sm" fullWidth sx={{ zIndex: myZ }}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <CallReceivedIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6">Receive Trade</Typography>
          </Box>
          <IconButton onClick={() => onClose(null)} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Vehicle</Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth size="small" label="VIN"
              value={form.vin || ''} onChange={handleChange('vin')}
              inputProps={{ style: { fontFamily: 'monospace', textTransform: 'uppercase' } }}
              helperText="Optional — add it later from the car page"
            />
          </Grid>

          <Grid item xs={3}>
            <TextField
              fullWidth size="small" label="Year"
              value={form.year || ''} onChange={handleChange('year')}
              required
            />
          </Grid>
          <Grid item xs={4.5}>
            <TextField
              fullWidth size="small" label="Make"
              value={form.make || ''} onChange={handleChange('make')}
              required
            />
          </Grid>
          <Grid item xs={4.5}>
            <TextField
              fullWidth size="small" label="Model"
              value={form.model || ''} onChange={handleChange('model')}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth size="small" label="Color"
              value={form.color || ''} onChange={handleChange('color')}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Trade Equity</Typography>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ bgcolor: 'grey.50', border: 1, borderColor: 'divider', borderRadius: 1, px: 1.5, py: 1.25, mb: 1 }}>
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="caption" color="text.secondary">Gross Trade Value</Typography>
                <Typography variant="caption">{USD(grossTrade)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="caption" color="text.secondary">Less Payoff</Typography>
                <Typography variant="caption">− {USD(payoff)}</Typography>
              </Box>
              <Divider sx={{ my: 0.75 }} />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="caption" sx={{ fontWeight: 700 }}>Net Equity</Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, color: netTradePreview >= 0 ? 'success.main' : 'error.main' }}>
                  {USD(netTradePreview)}
                </Typography>
              </Box>
            </Box>

            <TextField
              fullWidth size="small" label="Equity Amount"
              type="number"
              value={form.equity || ''} onChange={handleChange('equity')}
              helperText="Pre-filled from net trade. Override if needed."
              required
            />
          </Grid>

          <Grid item xs={12}>
            <Alert severity="info" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
              <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.8 }}>
                This will atomically:<br />
                • Create a new inventory record assigned to Skyway Classics<br />
                • Add deposit on <strong>{currentStock}</strong> — "Trade equity for [new stock]"<br />
                • Add purchase cost on new stock — "Trade equity from {currentStock}"
              </Typography>
            </Alert>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => onClose(null)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={saving}
          startIcon={<CallReceivedIcon />}
        >
          {saving ? 'Processing...' : 'Receive Trade'}
        </Button>
      </DialogActions>
    </Dialog>
    </ModalStackProvider>
  );
}
